/**
 * Recommendation lifecycle UI (Phase 4.0, OPTIMISATION-DETECTORS-PLAN.md).
 *
 * One state per item, shown the same way on every screen (gap 21): the
 * badge, the group chips, the allowed action buttons, the reject / snooze
 * dialogs and the state timeline all come from optimization-api's
 * GET /recommendations/states catalogue, so no screen hard-codes a status
 * list - and an unknown value shows as itself, never silently as "Pending"
 * (gap 15). FALLBACK_CATALOGUE is used only if that call fails.
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle, BadgeCheck, Check, CheckCheck, CheckCircle2, Circle, Clock, ExternalLink, Loader2, Moon, Search, Ticket, Wrench, X,
} from 'lucide-react';
import { attachAuthRefresh } from '../../services/apiClient';
import { authHeaders } from '../../services/apiHeaders';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { cn } from '../../utils/cn';

const OPTIMIZATION_API_URL = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';
export const lifecycleApi = attachAuthRefresh(axios.create({ baseURL: OPTIMIZATION_API_URL }));

export type StateGroup = 'to_review' | 'in_flight' | 'needs_attention' | 'saved' | 'set_aside';

export interface StateDef { value: string; label: string; description: string; group: StateGroup; money: string; color: string; icon: string }
export interface StateAction { action: string; label: string; to: string; from: string[]; role: 'operator' | 'approver' | 'system' }
export interface StateCatalogue {
  states: StateDef[];
  allowed_moves: Record<string, string[]>;
  actions: StateAction[];
  reject_reasons: Array<{ value: string; label: string; noteRequired: boolean }>;
  max_snooze_days: number;
  groups: Array<{ value: StateGroup; label: string }>;
  default_groups: StateGroup[];
}

/** Lifecycle fields the list / detail endpoints return on a recommendation. */
export interface LifecycleFields {
  id: string;
  status?: string;
  metadata?: Record<string, any>;
  saved_monthly?: number | null;
  reject_reason?: string | null;
  status_note?: string | null;
  snoozed_until?: string | null;
  status_changed_by?: string | null;
  status_changed_at?: string | null;
}

const FALLBACK_CATALOGUE: StateCatalogue = {
  states: [
    { value: 'open', label: 'Open', description: 'Found by a check, not looked at yet', group: 'to_review', money: 'identified', color: 'gray', icon: 'circle' },
    { value: 'in_review', label: 'In review', description: 'Someone is reviewing the finding and its fix', group: 'to_review', money: 'identified', color: 'blue', icon: 'search' },
    { value: 'accepted', label: 'Accepted', description: 'The fix was agreed and should be done', group: 'in_flight', money: 'in_progress', color: 'indigo', icon: 'check' },
    { value: 'ticketed', label: 'Ticketed', description: 'A ticket was raised for the fix', group: 'in_flight', money: 'in_progress', color: 'purple', icon: 'ticket' },
    { value: 'fixing', label: 'In progress', description: 'The fix is being worked on', group: 'in_flight', money: 'in_progress', color: 'amber', icon: 'wrench' },
    { value: 'done', label: 'Done', description: 'The fix was carried out', group: 'in_flight', money: 'in_progress', color: 'teal', icon: 'check-circle' },
    { value: 'verifying', label: 'Verifying', description: 'Watching the bill to confirm the saving', group: 'in_flight', money: 'in_progress', color: 'teal', icon: 'clock' },
    { value: 'verified', label: 'Verified', description: 'The bill shows the saving', group: 'saved', money: 'saved', color: 'green', icon: 'badge-check' },
    { value: 'not_verified', label: 'Not verified', description: 'The charge is still on the bill after the fix', group: 'needs_attention', money: 'in_progress', color: 'red', icon: 'alert' },
    { value: 'rejected', label: 'Rejected', description: 'Will not be fixed, with a reason', group: 'set_aside', money: 'excluded', color: 'slate', icon: 'x' },
    { value: 'snoozed', label: 'Snoozed', description: 'Hidden until a date, then opens again', group: 'set_aside', money: 'excluded', color: 'gray', icon: 'moon' },
    { value: 'resolved', label: 'Resolved', description: 'No longer detected - resource removed or fixed outside FinOps', group: 'saved', money: 'saved', color: 'emerald', icon: 'check-double' },
  ],
  allowed_moves: {},
  actions: [
    { action: 'start_review', label: 'Investigate', to: 'in_review', from: ['open'], role: 'operator' },
    { action: 'accept', label: 'Accept fix', to: 'accepted', from: ['in_review', 'not_verified'], role: 'approver' },
    { action: 'create_ticket', label: 'Create ticket', to: 'ticketed', from: ['open', 'in_review', 'accepted'], role: 'operator' },
    { action: 'mark_in_progress', label: 'Mark in progress', to: 'fixing', from: ['ticketed'], role: 'operator' },
    { action: 'mark_done', label: 'Mark done', to: 'done', from: ['accepted', 'ticketed', 'fixing'], role: 'operator' },
    { action: 'reject', label: 'Reject', to: 'rejected', from: ['open', 'in_review'], role: 'approver' },
    { action: 'snooze', label: 'Snooze', to: 'snoozed', from: ['open', 'in_review'], role: 'operator' },
    { action: 'reopen', label: 'Reopen', to: 'open', from: ['rejected', 'snoozed'], role: 'approver' },
  ],
  reject_reasons: [
    { value: 'not_applicable', label: 'Not applicable', noteRequired: false },
    { value: 'accepted_risk', label: 'Accepted risk', noteRequired: true },
    { value: 'business_need', label: 'Needed by the business', noteRequired: false },
    { value: 'wrong_finding', label: 'Wrong finding', noteRequired: false },
    { value: 'duplicate', label: 'Duplicate', noteRequired: false },
    { value: 'other', label: 'Other', noteRequired: true },
  ],
  max_snooze_days: 180,
  groups: [
    { value: 'to_review', label: 'To review' },
    { value: 'in_flight', label: 'In flight' },
    { value: 'needs_attention', label: 'Needs attention' },
    { value: 'saved', label: 'Saved' },
    { value: 'set_aside', label: 'Set aside' },
  ],
  default_groups: ['to_review', 'in_flight', 'needs_attention'],
};

/** System-only reasons the user list leaves out, still needed to label history. */
const SYSTEM_REASON_LABELS: Record<string, string> = {
  ticket_wont_fix: "Ticket closed as Won't Fix",
  legacy_no_reason: 'Rejected before reasons were recorded',
};

let cataloguePromise: Promise<StateCatalogue> | null = null;
function loadCatalogue(): Promise<StateCatalogue> {
  if (!cataloguePromise) {
    cataloguePromise = lifecycleApi
      .get('/api/v1/recommendations/states', { headers: authHeaders() })
      .then((res) => (res.data?.data as StateCatalogue) || FALLBACK_CATALOGUE)
      .catch(() => {
        cataloguePromise = null; // try again next time
        return FALLBACK_CATALOGUE;
      });
  }
  return cataloguePromise;
}

export function useStateCatalogue(): StateCatalogue {
  const [catalogue, setCatalogue] = useState<StateCatalogue>(FALLBACK_CATALOGUE);
  useEffect(() => {
    let alive = true;
    loadCatalogue().then((c) => alive && setCatalogue(c));
    return () => { alive = false; };
  }, []);
  return catalogue;
}

// Colour + icon + word, never colour alone (accessibility).
const COLOR_CLASSES: Record<string, string> = {
  gray: 'bg-muted text-muted-foreground',
  blue: 'bg-sky-500/10 text-sky-700 dark:text-sky-400',
  indigo: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400',
  purple: 'bg-purple-500/10 text-purple-700 dark:text-purple-400',
  amber: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  teal: 'bg-teal-500/10 text-teal-700 dark:text-teal-400',
  green: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  emerald: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-1 ring-inset ring-emerald-500/30',
  red: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
  slate: 'bg-slate-500/15 text-slate-700 dark:text-slate-300',
};
const ICONS: Record<string, React.ElementType> = {
  circle: Circle, search: Search, check: Check, ticket: Ticket, wrench: Wrench, 'check-circle': CheckCircle2,
  clock: Clock, 'badge-check': BadgeCheck, alert: AlertTriangle, x: X, moon: Moon, 'check-double': CheckCheck,
};

export function stateDef(catalogue: StateCatalogue, value?: string): StateDef {
  return (
    catalogue.states.find((s) => s.value === value) ||
    // Unknown value: show it as itself rather than pretending it is Open/Pending.
    { value: value || 'unknown', label: (value || 'unknown').replace(/_/g, ' '), description: 'Unrecognised state', group: 'to_review', money: 'identified', color: 'gray', icon: 'alert' }
  );
}

export function rejectReasonLabel(catalogue: StateCatalogue, value?: string | null): string {
  if (!value) return '';
  return catalogue.reject_reasons.find((r) => r.value === value)?.label || SYSTEM_REASON_LABELS[value] || value.replace(/_/g, ' ');
}

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/** The one-line detail shown beside the state: ticket key, snooze date, saved amount, reason. */
export function stateDetail(rec: LifecycleFields, catalogue: StateCatalogue): string | null {
  const md = rec.metadata || {};
  switch (rec.status) {
    case 'ticketed':
    case 'fixing': {
      const key = md.ticket?.external_key || md.approval_action?.ticket?.external_key;
      return key ? String(key) : null;
    }
    case 'snoozed':
      return rec.snoozed_until ? `until ${shortDate(rec.snoozed_until)}` : null;
    case 'verified':
    case 'resolved': {
      if (rec.saved_monthly && rec.saved_monthly > 0) return `${money(rec.saved_monthly)}/mo saved`;
      const kind = md.resolution?.kind;
      if (kind === 'unmeasured' || md.verification_result?.basis === 'no_longer_detected') return 'saving not measured';
      if (kind === 'no_longer_applies') return 'no longer applies';
      return null;
    }
    case 'rejected':
      return rejectReasonLabel(catalogue, rec.reject_reason) || null;
    case 'verifying': {
      const started = md.verification?.started_at;
      if (!started) return null;
      const day = Math.max(0, Math.floor((Date.now() - new Date(started).getTime()) / 86_400_000));
      return `day ${day} of 10`;
    }
    default:
      return null;
  }
}

export const StateBadge: React.FC<{ rec: LifecycleFields; catalogue: StateCatalogue; showDetail?: boolean; className?: string }> = ({
  rec, catalogue, showDetail = true, className,
}) => {
  const def = stateDef(catalogue, rec.status);
  const Icon = ICONS[def.icon] || Circle;
  const detail = showDetail ? stateDetail(rec, catalogue) : null;
  return (
    <span
      className={cn('inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', COLOR_CLASSES[def.color] || COLOR_CLASSES.gray, className)}
      title={`${def.label} - ${def.description}${detail ? ` (${detail})` : ''}`}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{def.label}{detail ? ` · ${detail}` : ''}</span>
    </span>
  );
};

/** Group chips for the list (gap 21). `null` = the default view. */
export const StateGroupChips: React.FC<{
  catalogue: StateCatalogue;
  value: StateGroup | 'all' | null;
  onChange: (next: StateGroup | 'all' | null) => void;
}> = ({ catalogue, value, onChange }) => {
  const chip = (key: StateGroup | 'all' | null, label: string) => (
    <button
      key={String(key)}
      type="button"
      onClick={() => onChange(key)}
      aria-pressed={value === key}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        value === key ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-muted-foreground hover:bg-secondary'
      )}
    >
      {label}
    </button>
  );
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by state group">
      {chip(null, 'Active')}
      {catalogue.groups.map((g) => chip(g.value, g.label))}
      {chip('all', 'All')}
    </div>
  );
};

/** Buttons for the moves allowed from this state (the server re-checks role and state). */
export function allowedActions(catalogue: StateCatalogue, status?: string): StateAction[] {
  return catalogue.actions.filter((a) => a.from.includes(status || ''));
}

function idempotencyHeaders(): Record<string, string> {
  return { ...authHeaders(), 'Idempotency-Key': crypto.randomUUID() };
}

function errorText(err: any, fallback: string): string {
  return err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;
}

/** POST a state-only action (accept, mark_in_progress, mark_done, reject, snooze, reopen). */
export async function runStateAction(id: string, action: string, body: Record<string, unknown> = {}): Promise<{ status: string }> {
  const res = await lifecycleApi.post(`/api/v1/recommendations/${id}/actions/${action}`, body, { headers: idempotencyHeaders() });
  return res.data?.data;
}

export async function createTicketFromItem(id: string): Promise<{ status: string; ticket: { external_key?: string | null; external_url?: string | null } }> {
  const res = await lifecycleApi.post(`/api/v1/recommendations/${id}/create-ticket`, {}, { headers: idempotencyHeaders() });
  return res.data?.data;
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

export const RejectDialog: React.FC<{ catalogue: StateCatalogue; recId: string; onClose: () => void; onDone: (status: string) => void }> = ({
  catalogue, recId, onClose, onDone,
}) => {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selected = catalogue.reject_reasons.find((r) => r.value === reason);
  const canSubmit = Boolean(selected) && (!selected?.noteRequired || note.trim().length > 0) && !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await runStateAction(recId, 'reject', { reason, note: note.trim() || undefined });
      onDone(data?.status || 'rejected');
    } catch (err: any) {
      setError(errorText(err, 'Could not reject'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Reject recommendation" subtitle="It moves to Set aside with your reason, and can be reopened later." maxWidth="max-w-md">
      <div className="space-y-3">
        <label className="block text-xs font-medium text-foreground/80">
          Reason
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-card px-2 text-sm">
            <option value="">Choose a reason…</option>
            {catalogue.reject_reasons.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </label>
        <label className="block text-xs font-medium text-foreground/80">
          Note {selected?.noteRequired ? <span className="text-rose-600">(required)</span> : <span className="text-muted-foreground">(optional)</span>}
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} className="mt-1 w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm" />
        </label>
        {error && <p className="text-sm text-rose-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={submit} disabled={!canSubmit}>
            {busy && <Loader2 className="animate-spin" />} Reject
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const SnoozeDialog: React.FC<{ catalogue: StateCatalogue; recId: string; onClose: () => void; onDone: (status: string) => void }> = ({
  catalogue, recId, onClose, onDone,
}) => {
  const isoDay = (daysAhead: number) => new Date(Date.now() + daysAhead * 86_400_000).toISOString().slice(0, 10);
  const [preset, setPreset] = useState<'7' | '30' | '90' | 'date'>('7');
  const [customDate, setCustomDate] = useState(isoDay(14));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const untilIso = useMemo(() => {
    if (preset !== 'date') return new Date(Date.now() + Number(preset) * 86_400_000).toISOString();
    // Wake at 09:00 local time on the chosen day.
    return new Date(`${customDate}T09:00:00`).toISOString();
  }, [preset, customDate]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await runStateAction(recId, 'snooze', { until: untilIso, note: note.trim() || undefined });
      onDone(data?.status || 'snoozed');
    } catch (err: any) {
      setError(errorText(err, 'Could not snooze'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Snooze recommendation" subtitle="Hidden under Set aside until the date, then it opens again automatically." maxWidth="max-w-md">
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Snooze for">
          {(['7', '30', '90'] as const).map((d) => (
            <button key={d} type="button" role="radio" aria-checked={preset === d} onClick={() => setPreset(d)}
              className={cn('rounded-full border px-3 py-1 text-xs', preset === d ? 'border-primary bg-primary text-primary-foreground' : 'border-border')}
            >
              {d} days
            </button>
          ))}
          <button type="button" role="radio" aria-checked={preset === 'date'} onClick={() => setPreset('date')}
            className={cn('rounded-full border px-3 py-1 text-xs', preset === 'date' ? 'border-primary bg-primary text-primary-foreground' : 'border-border')}
          >
            Pick a date
          </button>
        </div>
        {preset === 'date' && (
          <input type="date" value={customDate} min={isoDay(1)} max={isoDay(catalogue.max_snooze_days)} onChange={(e) => setCustomDate(e.target.value)}
            className="h-9 w-full rounded-md border border-border bg-card px-2 text-sm" aria-label="Snooze until" />
        )}
        <p className="text-xs text-muted-foreground">Opens again on {shortDate(untilIso)}.</p>
        <label className="block text-xs font-medium text-foreground/80">
          Note <span className="text-muted-foreground">(optional)</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} className="mt-1 w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm" />
        </label>
        {error && <p className="text-sm text-rose-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy}>
            {busy && <Loader2 className="animate-spin" />} Snooze
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Detail page: state bar + timeline
// ---------------------------------------------------------------------------

/**
 * The item's current state, its details, and only the buttons allowed now.
 * Investigate keeps its own button in the Investigation tab (it runs the
 * live preview); everything else is here.
 */
export const LifecycleBar: React.FC<{
  rec: LifecycleFields;
  catalogue: StateCatalogue;
  onChanged: (message?: string) => void;
}> = ({ rec, catalogue, onChanged }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'reject' | 'snooze' | null>(null);
  const md = rec.metadata || {};
  const def = stateDef(catalogue, rec.status);
  const actions = allowedActions(catalogue, rec.status).filter((a) => a.action !== 'start_review');
  const ticket = md.ticket || md.approval_action?.ticket;

  const run = async (action: StateAction) => {
    if (action.action === 'reject' || action.action === 'snooze') {
      setDialog(action.action);
      return;
    }
    setBusy(action.action);
    setError(null);
    try {
      if (action.action === 'create_ticket') {
        const data = await createTicketFromItem(rec.id);
        onChanged(`Ticket created${data?.ticket?.external_key ? `: ${data.ticket.external_key}` : ''}`);
      } else {
        await runStateAction(rec.id, action.action);
        onChanged(`${action.label} - done`);
      }
    } catch (err: any) {
      setError(errorText(err, `Could not ${action.label.toLowerCase()}`));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mb-3 rounded-lg border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">State</span>
        <StateBadge rec={rec} catalogue={catalogue} className="text-sm" />
        <span className="text-xs text-muted-foreground">{def.description}</span>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {actions.map((a) => (
            <Button
              key={a.action}
              size="sm"
              variant={a.action === 'reject' ? 'danger' : a.action === 'accept' || a.action === 'create_ticket' ? 'primary' : 'outline'}
              onClick={() => run(a)}
              disabled={busy !== null}
              title={a.role === 'approver' ? 'Needs a FinOps admin' : 'Needs an analyst or admin'}
            >
              {busy === a.action && <Loader2 className="animate-spin" />}
              {a.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {rec.status_changed_at && (
          <span>Since {shortDate(rec.status_changed_at)}{rec.status_changed_by && !rec.status_changed_by.startsWith('system:') ? ` · by ${rec.status_changed_by}` : ''}</span>
        )}
        {ticket?.external_key && (
          <span>
            Ticket{' '}
            {ticket.external_url ? (
              <a href={ticket.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-primary">
                {ticket.external_key} <ExternalLink className="h-3 w-3" />
              </a>
            ) : ticket.external_key}
          </span>
        )}
        {rec.status === 'rejected' && rec.status_note && <span>Note: {rec.status_note}</span>}
        {md.sla?.fix_escalated_at && <span className="text-amber-700 dark:text-amber-400">Fix overdue (over {md.sla.fix_sla_days} days since ticketed)</span>}
        {md.came_back && <span className="text-amber-700 dark:text-amber-400">Came back on {shortDate(md.came_back.at)} after being {md.came_back.from}</span>}
        {(rec.status === 'resolved' || rec.status === 'verified') && (md.resolution || md.verification_result) && (
          <SavingProof resolution={md.resolution} verification={md.verification_result} />
        )}
      </div>

      {error && <p className="mt-2 text-sm text-rose-600">{error}</p>}
      {dialog === 'reject' && (
        <RejectDialog catalogue={catalogue} recId={rec.id} onClose={() => setDialog(null)} onDone={() => { setDialog(null); onChanged('Rejected'); }} />
      )}
      {dialog === 'snooze' && (
        <SnoozeDialog catalogue={catalogue} recId={rec.id} onClose={() => setDialog(null)} onDone={() => { setDialog(null); onChanged('Snoozed'); }} />
      )}
    </div>
  );
};

/** How the saving was proven - shown so a "Saved" number is never unexplained. */
const SavingProof: React.FC<{ resolution?: any; verification?: any }> = ({ resolution, verification }) => {
  const src = verification?.basis === 'bill' ? verification : resolution;
  if (!src) return null;
  const KIND: Record<string, string> = {
    removed: 'resource no longer on the bill',
    fixed_outside: 'fixed outside FinOps',
    no_longer_applies: 'no longer applies - cost did not drop, $0 counted',
    unmeasured: 'could not be matched to the bill - $0 counted',
  };
  if (verification?.basis === 'no_longer_detected') return <span>Verified: no longer detected (saving not measured)</span>;
  const kind = resolution && !verification ? KIND[resolution.kind] || resolution.kind : 'checked on the bill';
  const before = src.baseline_daily ?? src.baseline?.daily_cost;
  const after = src.after_daily ?? src.after?.daily_cost;
  return (
    <span>
      Proof: {kind}
      {before != null && after != null && ` · daily cost ${money(Number(before))} → ${money(Number(after))}`}
      {resolution?.counted_on && ' · saving counted on another finding for the same resource'}
    </span>
  );
};

interface HistoryRow { from_status: string | null; to_status: string; actor: string; reason: string | null; note: string | null; ticket_key: string | null; created_at: string }

/** Who did what, when, and why (plan: audit + Investigate timeline). */
export const StateTimeline: React.FC<{ recId: string; catalogue: StateCatalogue; refreshKey: number }> = ({ recId, catalogue, refreshKey }) => {
  const [rows, setRows] = useState<HistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    lifecycleApi
      .get(`/api/v1/recommendations/${recId}/history`, { headers: authHeaders() })
      .then((res) => alive && setRows(res.data?.data || []))
      .catch((err) => alive && setError(errorText(err, 'Could not load history')));
    return () => { alive = false; };
  }, [recId, refreshKey]);

  if (error) return <p className="text-sm text-muted-foreground">{error}</p>;
  if (!rows) return <p className="text-sm text-muted-foreground">Loading history…</p>;
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">No state changes recorded yet.</p>;

  const who = (actor: string) => {
    if (!actor.startsWith('system:')) return actor;
    const SYSTEM: Record<string, string> = {
      'system:ticket-sync': 'ticket sync', 'system:resolver': 'daily scan', 'system:verification': 'bill check',
      'system:snooze-wake': 'snooze ended', 'system:redetected': 'detected again', 'system:reopen': 'review timer',
      'system:migration': 'migration', 'system:l1': 'automatic action',
    };
    return SYSTEM[actor] || actor.slice(7);
  };

  return (
    <ol className="space-y-2">
      {rows.map((r, i) => (
        <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="w-36 shrink-0 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
          <StateBadge rec={{ id: recId, status: r.to_status }} catalogue={catalogue} showDetail={false} />
          <span className="text-xs text-muted-foreground">
            {r.from_status ? `from ${stateDef(catalogue, r.from_status).label} · ` : ''}{who(r.actor)}
            {r.reason ? ` · ${rejectReasonLabel(catalogue, r.reason)}` : ''}
            {r.ticket_key ? ` · ${r.ticket_key}` : ''}
          </span>
          {r.note && <span className="w-full pl-36 text-xs text-foreground/80">“{r.note}”</span>}
        </li>
      ))}
    </ol>
  );
};
